import { useEffect, useState } from "react";
import ErrorMessage from "../../../../Components/Common/ErrorMessage/ErrorMessage";
import { EMAIL_REGEX } from "../../../../utils/Common/Constant";
import PrevIcon from "Assets/Img/PrevIcon";
import Button from "../../../../Components/Common/Button/Button";
import SectionCard from "./SectionCard";
import NextIcon from "Assets/Img/NextIcon";
import WaitingIcon from "Assets/Img/WaitingIcon";
import StepTick from "Assets/Img/StepTick";
import AddSubcontractorModal from "./AddSubcontractorModal";
import { getDraw, inviteLienWaiver } from "../../../../services/drawService";
import { updateSubContractor } from "../../../../services/subContractorService";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import Input from "../../../../Components/Common/Input/Input";

const LienWaiversStepSkeleton = () => (
  <div className="subLineWaiverSec">
    {[0, 1, 2].map((i) => (
      <div className="subRow" key={i}>
        <div className="subBody">
          <Skeleton width={140} height={13} borderRadius={4} />
          <Skeleton
            width={200}
            height={11}
            borderRadius={4}
            style={{ marginTop: 6 }}
          />
        </div>
        <Skeleton width={120} height={13} borderRadius={4} />
        <div className="subActions">
          <Skeleton width={90} height={32} borderRadius={6} />
          <Skeleton width={64} height={32} borderRadius={6} />
        </div>
      </div>
    ))}
  </div>
);

type LienStatus = "signed" | "pending" | "notSent";

interface LienSub {
  id: string;
  lienWaiverId: string;
  name: string;
  email: string;
  amount: string;
  status: LienStatus;
  signedDate: string;
  sending: boolean;
  subContractorId: string | null;
}

interface LienWaiversStepProps {
  projectId: string;
  drawId: string;
  bankCompanyName: string;
  onDrawRefresh?: (draw: any) => void;
  onBack: () => void;
  onNext: () => void;
}

const formatAmount = (amountObj: any): string => {
  if (!amountObj) return "-";
  const symbol = amountObj.currency?.symbol ?? "$";
  return `${symbol}${Number(amountObj.amount ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
};

const formatDate = (val: string | null): string => {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d.getTime())
    ? val
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const mapLienItems = (lienItems: any[], drawDate: string | null): LienSub[] =>
  lienItems.map((item) => {
    const waiver = item.lienWaivers?.[0];
    let status: LienStatus = "notSent";
    if (waiver?.signedAt) status = "signed";
    else if (waiver?.invitedAt) status = "pending";
    return {
      id: item.id,
      lienWaiverId: waiver?.id ?? "",
      name: item.subContractor?.companyName ?? "",
      email: item.subContractor?.email ?? "",
      amount: formatAmount(item.totalAmount),
      status,
      signedDate: formatDate(waiver?.signedAt ?? drawDate),
      sending: false,
      subContractorId: item.subContractorId ?? null,
    };
  });

const LienWaiversStep = ({
  projectId,
  drawId,
  bankCompanyName,
  onDrawRefresh,
  onBack,
  onNext,
}: LienWaiversStepProps) => {
  const showToast = useToast();
  const [lienSubs, setLienSubs] = useState<LienSub[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const [editEmailValue, setEditEmailValue] = useState("");
  const [savingEmailId, setSavingEmailId] = useState<string | null>(null);
  const [emailError, setEmailError] = useState("");
  const fetchDraw = async () => {
    const { data, error } = (await getDraw(projectId, drawId)) as {
      data: any;
      error: any;
    };
    if (error || !data) return;
    const draw = data?.data ?? data;
    setLienSubs(mapLienItems(draw?.lienItems ?? [], draw?.drawDate ?? null));
    onDrawRefresh?.(draw);
  };

  useEffect(() => {
    if (!projectId || !drawId) return;
    setLoading(true);
    fetchDraw().finally(() => setLoading(false));
    const interval = setInterval(fetchDraw, 10000);
    return () => clearInterval(interval);
  }, [projectId, drawId]);

  const handleSendWaiver = async (id: string) => {
    const sub = lienSubs.find((s) => s.id === id);
    if (!sub?.lienWaiverId) return;
    setLienSubs((prev) =>
      prev.map((s) => (s.id === id ? { ...s, sending: true } : s)),
    );
    try {
      const { error } = (await inviteLienWaiver(
        projectId,
        drawId,
        sub.lienWaiverId,
      )) as { error: any };
      if (error) throw new Error(error.message || "Failed to send waiver");
      await fetchDraw();
      showToast("Lien waiver sent successfully");
    } catch (err: any) {
      setLienSubs((prev) =>
        prev.map((s) => (s.id === id ? { ...s, sending: false } : s)),
      );
      showToast(err.message || "Failed to send waiver");
    }
  };

  // const handlePreviewWaiver = (_name: string, _amount: string) => {};

  const handleEditEmailStart = (sub: LienSub) => {
    setEditingEmailId(sub.id);
    setEditEmailValue(sub.email);
    setEmailError("");
  };

  const handleSaveEmail = async (sub: LienSub) => {
    const trimmed = editEmailValue.trim();
    if (!trimmed) {
      setEmailError("Email is required");
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError("Enter a valid email address");
      return;
    }
    if (trimmed === sub.email) {
      setEditingEmailId(null);
      setEmailError("");
      return;
    }
    setEmailError("");
    setSavingEmailId(sub.id);
    try {
      if (!sub.subContractorId) return;
      const { error } = (await updateSubContractor(sub.subContractorId, {
        email: trimmed,
      })) as { error: any };
      if (error) throw new Error(error.message || "Failed to update email");
      showToast("Email updated successfully");
      setEditingEmailId(null);
      setLoading(true);
      await fetchDraw().finally(() => setLoading(false));
    } catch (err: any) {
      showToast(err.message || "Failed to update email");
    } finally {
      setSavingEmailId(null);
    }
  };

  const handleAddSubcontractor = (payload: {
    name: string;
    email: string;
    phone: string;
    amount: string;
  }) => {
    const newSub: LienSub = {
      id: String(Date.now()),
      lienWaiverId: "",
      name: payload.name,
      email: payload.email,
      amount: payload.amount || "$0.00",
      status: "notSent",
      signedDate: "",
      sending: false,
      subContractorId: null,
    };
    setLienSubs((prev) => [...prev, newSub]);
    setModalOpen(false);
  };

  const pendingCount = lienSubs.filter((s) => s.status !== "signed").length;

  return (
    <section
      className="screen activeScreen"
      aria-labelledby="lien-waiver-title"
    >
      <h2 className="sectionTitle" id="lien-waiver-title">
        Lien Waiver Collection
      </h2>
      <p className="sectionSub">
        {bankCompanyName || "The bank"} requires lien waivers for all
        subcontractors being paid in this draw.
      </p>

      <SectionCard title="Lien Waiver Status">
        <div className="infoBox">
          <strong>How it works:</strong> Enter or select each subcontractor
          below. The platform will pre-fill their lien waiver with this draw's
          project name, address, owner, draw amount, and period. They receive a
          secure link — no login needed — to review, complete any blanks, and
          sign on their phone or computer. Status updates here in real time.
        </div>

        {loading ? (
          <LienWaiversStepSkeleton />
        ) : (
          <div className="subLineWaiverSec">
            {lienSubs.map((sub) => {
              const isSigned = sub.status === "signed";
              const statusLabel =
                sub.status === "signed" ? (
                  <>
                    <StepTick /> {`Signed — ${sub.signedDate || "On File"}`}
                  </>
                ) : sub.status === "pending" ? (
                  <>
                    <WaitingIcon /> Sent — awaiting signature
                  </>
                ) : (
                  "Not sent"
                );

              return (
                <div className="subRow" key={sub.id}>
                  <div className="subBody">
                    <p className="subName">{sub.name}</p>
                    <p className="subMeta">
                      {editingEmailId === sub.id ? (
                        <>
                          <div>
                            <Input
                              className={`emailEditInput${emailError ? " error-input-red" : ""}`}
                              type="email"
                              value={editEmailValue}
                              onChange={(e) => {
                                const val = e.target.value;
                                setEditEmailValue(val);
                                if (!val) setEmailError("Email is required");
                                else if (!EMAIL_REGEX.test(val))
                                  setEmailError("Enter a valid email address");
                                else setEmailError("");
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveEmail(sub);
                                if (e.key === "Escape") {
                                  setEditingEmailId(null);
                                  setEmailError("");
                                }
                              }}
                              autoFocus
                              disabled={savingEmailId === sub.id}
                            />
                            <ErrorMessage
                              touched={!!emailError}
                              error={emailError}
                            />
                          </div>
                          <Button
                            className="emailEditSave"
                            onClick={() => handleSaveEmail(sub)}
                            disabled={savingEmailId === sub.id}
                          >
                            {savingEmailId === sub.id ? "Saving…" : "Save"}
                          </Button>
                          <Button
                            className="emailEditCancel"
                            onClick={() => {
                              setEditingEmailId(null);
                              setEmailError("");
                            }}
                            disabled={savingEmailId === sub.id}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          {sub.email || "No email"}{" "}
                          <Button
                            className="emailEditTrigger"
                            onClick={() => handleEditEmailStart(sub)}
                            title="Edit email"
                          >
                            ✎
                          </Button>{" "}
                          · {sub.amount}
                        </>
                      )}
                    </p>
                  </div>
                  <span
                    className={`subStatus ${isSigned ? "signed" : "pending"}`.trim()}
                  >
                    {statusLabel}
                  </span>
                  <div className="subActions">
                    {isSigned ? (
                      <Button className="invBtn attached">
                        Waiver on file
                      </Button>
                    ) : (
                      <Button
                        className={`lienBtn ${sub.status === "pending" ? "sent" : ""}`.trim()}
                        onClick={() => handleSendWaiver(sub.id)}
                        disabled={sub.sending}
                      >
                        {sub.sending
                          ? "Sending..."
                          : sub.status === "pending"
                            ? "Resend"
                            : "Send Waiver"}
                      </Button>
                    )}
                    <Button
                      className="invBtn"
                      onClick={() => {
                        window.open(
                          `/lien-waiver/preview/${projectId}/${drawId}/${sub.lienWaiverId}`,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                    >
                      Preview
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pendingCount > 0 && (
          <div className="warningBox">
            <strong>{pendingCount} waivers still outstanding.</strong> Waivers
            are due with this draw. You may submit the draw now and collect
            outstanding waivers before the bank releases funds, or wait until
            all are signed.
          </div>
        )}
      </SectionCard>

      <div className="btnRow spaced">
        <Button className="btn btnSecondary" onClick={onBack}>
          <PrevIcon /> Back
        </Button>
        <Button className="btn btnPrimary" onClick={onNext}>
          Next: Sign &amp; Submit <NextIcon />
        </Button>
      </div>

      <AddSubcontractorModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleAddSubcontractor}
      />
    </section>
  );
};

export default LienWaiversStep;
